// Grids & Gantts — a grid's config: its column kinds, its defaults and its
// normaliser.
//
// A grid is a structure, and a structure is a tree that assembles itself by
// running rules in order:
//
//   Filter work items   a query every level is passed through
//   Insert work items   a query whose answers become rows
//   Extend              how a row finds what hangs under it (the Parent
//                       field; work items linked by a named link type)
//   Group by            a field that brackets rows into group headers
//   Sort by             the order rows take
//
// The scope of a rule is where it sits. A rule at the TOP applies to the
// whole structure; a rule inside a FOLDER applies only inside that folder.
// That is the entire model, and everything below is data for it — held on
// the grid's row (config.structure) and edited in the page, in Stride admin
// mode, without touching this file.
//
// NOTHING ABOUT ANY CLIENT IS IN HERE. A grid starts EMPTY — no folders, no
// filter, no queries, no project keys, no status names — and is built in its
// own page. See ../../README.md.

// ---- what a column can be ----------------------------------------------------
// `kind` decides where a cell's value comes from; `agg` decides what a row
// with work underneath it shows instead of its own value.
export const COLUMN_KINDS = [
  { id: 'key', label: 'Key', agg: false, hint: 'The work item key — click it for the whole screen' },
  { id: 'summary', label: 'Summary (the tree column)', agg: false, hint: 'The indented tree with its twisties — a structure has exactly one' },
  { id: 'status', label: 'Status', agg: false },
  { id: 'type', label: 'Work type', agg: false },
  { id: 'date', label: 'Date', agg: true, hint: 'A date field. Span = earliest/latest of everything underneath' },
  { id: 'dueIn', label: 'Due In', agg: false, hint: 'How long is left, counted down the way the structure counts it' },
  { id: 'number', label: 'Number', agg: true, hint: 'A numeric field — Sum adds up everything underneath' },
  { id: 'text', label: 'Text', agg: false, hint: 'Any other field, as text' },
  { id: 'count', label: 'Count', agg: false, hint: 'Distinct work items in this branch, this row included' },
  { id: 'countSub', label: 'Count Sub-Items', agg: false, hint: 'Rows below this one — an item under two parents counts twice' },
  { id: 'progress', label: 'Progress', agg: false, hint: 'Percent of the work items underneath that are Done' },
  { id: 'countIf', label: 'Count where…', agg: false, hint: 'Work items in this branch that meet a condition (and are of the chosen work types)' },
  { id: 'ratio', label: 'Percent where…', agg: false, hint: 'Of the work items meeting “Out of”, the percent that also meet “Where” — e.g. Overdue out of Not Done' },
  { id: 'diff', label: 'Difference (A − B)', agg: false, hint: 'One column’s figure minus another’s, row by row' },
  // A ÷ B over the two columns' figures FOR THE ROW — on a folder, the ratio
  // of its totals (Σ Budget ÷ Σ TPV), which no formula can give: a formula
  // sees one work item at a time, and summing per-item ratios is nonsense.
  { id: 'divide', label: 'Ratio of two columns (A ÷ B, %)', agg: false, hint: 'One column’s figure as a percent of another’s, row by row — e.g. Σ Budget ÷ Σ TPV' },
  // The grouping formula's language (./grouping.js), one work item at a
  // time. Sum adds a numeric answer up over the distinct work items under a
  // row; without it a folder or a group shows nothing.
  { id: 'formula', label: 'Formula', agg: true, hint: 'A JavaScript expression over one work item — the same helpers as a formula grouping' },
  // EXTERNAL DATABASE — a value from an External Database Source (Settings →
  // Automation): the row of the source's table whose match column equals this
  // work item's field, and one column of it — VLOOKUP over a SharePoint file.
  // The page asks the source for every value at once (useLookups.js); the
  // cell only reads the answer (columns.js).
  { id: 'lookup', label: 'External database', agg: false, hint: 'A value from an External Database Source: the row whose match column equals this work item’s field (like VLOOKUP)' },
  // STOCK CHECK (./stock.js) — column A (what is in stock, normally an
  // External database column that adds up every matching row) against
  // column B (what the work needs), netted part by part in Due date order
  // over every row on the grid. A row it judges reads "Short 26" or "OK";
  // every row above reads how many short rows are under it.
  { id: 'stock', label: 'Stock check (in stock vs needed)', agg: false, hint: 'Column A (in stock) against column B (needed), part by part in Due date order — flags the rows that run short and counts them on every row above' },
];

export const SORTS = [
  { id: 'duedate', label: 'Due date' },
  { id: 'summary', label: 'Summary' },
  { id: 'key', label: 'Key' },
  { id: 'status', label: 'Status' },
  { id: 'type', label: 'Work type' },
  { id: 'start', label: 'Start date' },
  { id: 'none', label: 'Insertion order' },
];

export { FILTER_MODES } from './structure';

export const DONE_MODES = [
  { id: 'all', label: 'Show Done' },
  { id: 'hide', label: 'Hide Done' },
  { id: 'only', label: 'Done only' },
];

// A structure loaded by ONE broad query and assembled in memory
// ('universe'), or by walking level after level with a query per level
// ('walk').
//
// Universe costs one query per pageSize rows, however deep the tree goes,
// and is right when the structure covers most of the site. Walk costs one
// query per walkChunk parents per level, and loads ONLY what the tree
// reaches — which is right when the site is much bigger than the tree.
// Which one is right is a fact about the SITE; the admin's Data scope note
// prints the numbers to decide on.
export const LOAD_MODES = [
  // THE DEFAULT. The walk runs on the server against its own database and
  // answers in one streamed request with a compact payload — measured at a
  // tenth of the JSON and a hundredth of the requests of walking from the
  // browser. The two browser loaders stay for a site that cannot run it.
  { id: 'server', label: 'On the server, in one request (least data)' },
  { id: 'universe', label: 'In the browser: one broad load, assembled in memory' },
  { id: 'walk', label: 'In the browser: level by level, a query per level' },
];

// ---- an EMPTY structure — what every grid starts as -------------------------
//
// No client content of any kind: no folders, no filter, no insert queries,
// no project keys, no status names, no custom fields. What it DOES carry is
// the engine's own knobs and the six generic columns every structure needs
// before it can be read at all: the key, the tree, the status, the two dates
// the timeline draws from, and the count. They name built-ins or `duedate`,
// so they resolve on any site without a field being mapped first.
//
// KEEP THIS EMPTY. It is tempting to "helpfully" seed a folder or a filter
// here, and every grid minted afterwards then inherits one client's
// vocabulary without anyone noticing. The suite asserts it.
export const EMPTY_STRUCTURE = {
  // Structure scope — these apply to every folder below.
  filterZql: '',
  sortRoot: 'duedate',
  // What the structure filter means. See FILTER_MODES in structure.js.
  filterMode: 'keep-ancestors',
  // The safety valve on keep-ancestors: if the filter's complement is bigger
  // than this, loading it is not worth it and the page falls back to prune
  // (and says so in the admin's Data scope note).
  maxExcluded: 20000,

  loadMode: 'server',
  // What the one broad load asks for. Blank = the structure filter alone.
  universeZql: '',
  maxRows: 80000,
  pageSize: 5000,
  // How many parents one walk query asks about, and how many of a level's
  // queries are in flight at once. The ZQL goes by POST, so the query
  // length is not the constraint the chunk size is protecting.
  walkChunk: 300,
  walkParallel: 5,

  folders: [],

  columns: [
    { id: 'c-key', kind: 'key', label: 'Key', field: '', width: 104, align: 'left', agg: 'none', on: true, pinned: false },
    { id: 'c-sum', kind: 'summary', label: 'Summary', field: '', width: 340, align: 'left', agg: 'none', on: true, pinned: false },
    { id: 'c-status', kind: 'status', label: 'Status', field: '', width: 150, align: 'left', agg: 'none', on: true, pinned: false },
    { id: 'c-start', kind: 'date', label: 'Start date', field: 'Start date', width: 100, align: 'left', agg: 'none', on: true, pinned: false },
    { id: 'c-due', kind: 'date', label: 'Due date', field: 'duedate', width: 100, align: 'left', agg: 'none', on: true, pinned: false },
    { id: 'c-count', kind: 'count', label: 'Count', field: '', width: 72, align: 'right', agg: 'none', on: true, pinned: false },
  ],

  filters: [],
  buttons: [],
  buttonsMode: 'all',
  buttonsScope: 'tree',

  hideDone: 'all',
  openDepth: 1,        // the folders stand open, their contents shut
  showRules: false,    // the generator rows, off until the pin is pressed
  // The timeline is OFF on a page with nothing in it — there is no plan to
  // draw yet. Empty `columns` means "all of them" once it is on.
  // THE DEPENDENCY ARROWS. Four kinds (FS/FF/SS/SF), each naming the link
  // type that MEANS it on this site and which side of that link is the
  // predecessor. EMPTY BY DEFAULT and deliberately so: a grid draws an
  // arrow only for a rule its admin named. There is no inherited default
  // and no guess from a link's spelling — an unnamed rule draws nothing,
  // which is why an empty structure stays free of any client's vocabulary.
  //   { FS: { linkTypeId, linkTypeName, predecessorSide, lagDays }, ... }
  gantt: { on: false, columns: [], dependencies: {} },
  editable: true,      // cells edit in place
  // THE COLUMNS PINNED AT THE LEFT while the grid scrolls sideways — a row
  // of figures with no name against it cannot be read. null = the default:
  // the Key and the Summary columns. A list of column ids is the admin's
  // own choice (Page settings), and an empty list pins nothing.
  frozenColumns: null,
  // WHAT MAY BE CLONED from a row: the work TYPES an admin has opened up.
  // A grid starts with none, because a clone is forty creates and the admin
  // decides which shapes of work are worth that.
  cloneTypes: [],
  // The fields worth a second look before a copy is made — named by field
  // name, offered first in the clone dialog as one group that ticks with
  // one click. A grid starts with none; the admin names them.
  cloneAttention: [],
  // WHO MAY CLONE: the groups whose members get the Clone button and the
  // sheep on a row. Empty = everyone who may open the grid; admins always may.
  cloneGroups: [],
  // THE RESCHEDULER (./reschedule.js): a bar dragged whole takes the work
  // under it along. OFF until an admin switches it on; then site admins may
  // use it, plus the groups named here; and only on the work types listed,
  // each saying which of the work below stays put ({ type, skipDone,
  // skipInProgress }). A grid starts with it off and nothing listed.
  rescheduleOn: false,
  rescheduleGroups: [],
  rescheduleTypes: [],
  // HOW A COLUMN HEADING FITS. 'fit' (the old way) squeezes it into two or
  // three lines in smaller type; 'full' wraps it whole at full size and the
  // heading row grows to the longest heading.
  headWrap: 'fit',
  layouts: {},
};

// THE PRODUCT'S DEFAULTS ARE THE EMPTY STRUCTURE. A client fork that keeps a
// structure page of its own passes its own defaults to the component; the
// product never carries any.
export const DEFAULTS = EMPTY_STRUCTURE;

// ---- normalising -------------------------------------------------------------
import { FILTER_MODES } from './structure';
import { normalizeGroup, groupIsSet, formulaFieldNames } from './grouping';
export { GROUP_KINDS, DATE_GRAINS, PRESETS, describeGroup, groupIsSet } from './grouping';

const str = (v, d = '') => (typeof v === 'string' ? v : d);
const list = (v) => (Array.isArray(v) ? v : []);
const clone = (a) => JSON.parse(JSON.stringify(a));
const bool = (v, d) => (v === undefined ? d : !!v);
const int = (v, d, lo, hi) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(lo, Math.min(hi, Math.round(n))) : d;
};
const oneOf = (v, opts, d) => (opts.some((o) => (o.id || o) === v) ? v : d);
// A SORT is one of SORTS, or `field:<field name>` — any field, read the way
// a person reads it (numbers as numbers, dates as dates, text A to Z), empty
// values last. Named, not by id, like every other field in a config.
export const FIELD_SORT = 'field:';
export const sortFieldOf = (s) => (typeof s === 'string' && s.startsWith(FIELD_SORT) ? s.slice(FIELD_SORT.length).trim() : '');
const sortOf = (v, d) => (sortFieldOf(v) ? `${FIELD_SORT}${sortFieldOf(v)}` : oneOf(v, SORTS, d));
// A list of work item keys, however the admin typed it — commas, spaces or
// new lines, in any mixture, upper-cased and de-duplicated.
export const keyList = (v) => {
  const raw = Array.isArray(v) ? v.join(',') : String(v || '');
  const out = [];
  raw.split(/[\s,;]+/).forEach((k) => {
    const t = k.trim().toUpperCase();
    if (t && /^[A-Z][A-Z0-9_]*-\d+$/.test(t) && !out.includes(t)) out.push(t);
  });
  return out;
};
const uid = (p) => `${p}${Math.random().toString(36).slice(2, 7)}`;

export function normalizeLink(l, i = 0) {
  const r = l && typeof l === 'object' ? l : {};
  return {
    id: str(r.id) || `l${i}`,
    type: str(r.type, 'Link'),
    relation: str(r.relation, 'Link to Child'),
    levelFrom: int(r.levelFrom, 1, 1, 20),
    levelTo: int(r.levelTo, 20, 1, 20),
  };
}

// A folder's TABLE LINES rule (see ./lines.js): under every work item of
// `types`, the lines of the table in the rich-text field `field`, each a row
// called `label`; `key` and `text` name the table's columns that make a
// line's Summary, `skip` the lines left out (a column holding a value), and
// `map` which of the grid's fields read which of the table's columns. A rule
// with no field yet is KEPT (its amber row says so and opens the editor, as
// an unset grouping's does); it draws nothing until it names one.
export function normalizeLines(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const pairs = (v, a, b) => list(v).filter((x) => x && typeof x === 'object')
    .map((x) => ({ [a]: str(x[a]).trim(), [b]: str(x[b]).trim() })).filter((x) => x[a] && x[b]);
  return {
    field: str(raw.field).trim(),
    types: [...new Set(list(raw.types).map((t) => str(t).trim()).filter(Boolean))],
    label: str(raw.label).trim() || 'Line',
    key: str(raw.key).trim(),
    text: str(raw.text).trim(),
    skip: pairs(raw.skip, 'column', 'value'),
    map: pairs(raw.map, 'field', 'column'),
  };
}

// A folder's GROUP THE CHILDREN rule: { field, types } — null when it names no
// field (nothing to bracket by).
export function normalizeChildGroups(raw) {
  if (!raw || typeof raw !== 'object' || !str(raw.field).trim()) return null;
  return {
    field: str(raw.field).trim(),
    types: [...new Set(list(raw.types).map((t) => str(t).trim()).filter(Boolean))],
  };
}

export function normalizeFolder(f, i = 0) {
  const r = f && typeof f === 'object' ? f : {};
  const e = r.expand && typeof r.expand === 'object' ? r.expand : {};
  const inserts = r.inserts !== undefined
    ? list(r.inserts).map((q, j) => ({ id: str(q && q.id) || `i${j}`, zql: str(q && q.zql) }))
    : [{ id: 'i0', zql: str(r.insertZql) }];
  // An older config carried ONE link pair on the expand block; read it.
  const links = e.links !== undefined
    ? list(e.links).map(normalizeLink)
    : (e.linkTypes || e.childRelation
      ? [normalizeLink({ type: str(e.linkTypes, 'Link'), relation: str(e.childRelation, 'Link to Child') }, 0)]
      : []);
  return {
    id: str(r.id) || `f${i}`,
    name: str(r.name, `Folder ${i + 1}`),
    icon: str(r.icon, 'folder'),
    // A FOLDER MAY HAVE MORE THAN ONE INSERT — two queries that between them
    // say what belongs in a box, where no single query can. An older config
    // carried the single `insertZql`; it is read as the first (and only)
    // insert, so nothing saved before this changes shape or needs migrating.
    inserts,
    filterZql: str(r.filterZql),
    expand: {
      byParent: bool(e.byParent, true),
      links,
      maxDepth: int(e.maxDepth, 8, 1, 20),
      // TABLE LINES AS ROWS (./lines.js) — kept only when set, so a folder
      // saved before it normalises exactly as it always did.
      ...(normalizeLines(e.lines) ? { lines: normalizeLines(e.lines) } : {}),
    },
    // A GROUPING IS ONE OF FIVE KINDS (see ./grouping.js); a saved { id,
    // field } is the first kind, unchanged. One that is not set up yet — no
    // field, no buckets, no formula — is KEPT: it is the amber row that says
    // "(no field yet)" and opens the editor. Dropping it here (as the old
    // normaliser did) made "add a grouping" vanish before it could be
    // edited. The page skips an unset grouping when it brackets the rows.
    groups: list(r.groups).filter((g) => g && typeof g === 'object').map((g, j) => normalizeGroup(g, j)),
    // GROUP THE CHILDREN (a rule of its own — the groupings above bracket only
    // what the folder inserts): under EVERY row, its children of `types` (all
    // of them when none are named) bracketed by the value of `field`, one
    // header per value; the other children stay above the headers. Kept only
    // when it names a field.
    ...(normalizeChildGroups(r.groupChildren) ? { groupChildren: normalizeChildGroups(r.groupChildren) } : {}),
    sort: sortOf(r.sort, 'summary'),
    // A BARE FOLDER draws no row of its own: its rows (and the folders inside
    // it) sit where the folder would have been — a structure's top-level
    // generators, with no box around them. Its rules still print in admin
    // mode, under a row that says it is bare, so it can be edited.
    bare: bool(r.bare, false),
    openDepth: r.openDepth == null ? null : int(r.openDepth, 0, 0, 20),
    // The admin's hand on the folder's contents, over and above its query.
    // ALWAYS carried, whatever the insert query says; NEVER carried, however
    // it is reached. Both are work item keys.
    include: keyList(r.include),
    exclude: keyList(r.exclude),
    // A FOLDER MAY SIT INSIDE ANOTHER. The id of the folder it sits in, or
    // null at the top of the structure. Checked whole-structure in
    // normalizeConfig: an unknown parent, itself, or a cycle is the top.
    parentId: str(r.parentId) || null,
  };
}

// The parent links of a folder list, made safe: an unknown parent, a folder
// naming itself, or a cycle (A inside B inside A) puts the folder at the
// top rather than nowhere. Returns the same objects, fixed in place.
export function fixFolderParents(folders) {
  const known = new Set((folders || []).map((f) => f.id));
  const byId = new Map((folders || []).map((f) => [f.id, f]));
  (folders || []).forEach((f) => { if (f.parentId && (!known.has(f.parentId) || f.parentId === f.id)) f.parentId = null; });
  (folders || []).forEach((f) => {
    const seen = new Set([f.id]);
    let cur = f.parentId ? byId.get(f.parentId) : null;
    while (cur) {
      if (seen.has(cur.id)) { f.parentId = null; break; }
      seen.add(cur.id);
      cur = cur.parentId ? byId.get(cur.parentId) : null;
    }
  });
  return folders;
}

// The folders that sit inside `id` (null = the top), in the structure's own
// order, and everything under them — for an editor that must not offer a
// folder its own descendant as a home.
export function descendantFolderIds(folders, id) {
  const out = new Set();
  const walk = (pid) => (folders || []).forEach((f) => { if (f.parentId === pid && !out.has(f.id)) { out.add(f.id); walk(f.id); } });
  walk(id);
  return out;
}

export const newInsert = () => ({ id: uid('i'), zql: '' });

// A column's OPTIONAL shaping (see ./columns.js): kept only when set, so a
// column that uses none of it normalises exactly as it always did.
const WHERE_IDS = ['all', 'open', 'done', 'overdue', 'undated'];
const BAND_IDS = ['green', 'blue', 'amber', 'orange', 'red', 'purple', 'teal', 'grey'];
const names = (v) => [...new Set(list(v).map((x) => str(x).trim()).filter(Boolean))];
function columnOptions(r, kind) {
  const out = {};
  const types = names(r.types); const notTypes = names(r.notTypes);
  if (types.length) out.types = types;
  if (notTypes.length) out.notTypes = notTypes;
  if (kind === 'countIf' || kind === 'ratio') out.where = oneOf(r.where, WHERE_IDS, kind === 'ratio' ? 'overdue' : 'open');
  if (kind === 'ratio') out.of = oneOf(r.of, WHERE_IDS, 'open');
  if (kind === 'diff' || kind === 'divide' || kind === 'stock') { out.a = str(r.a); out.b = str(r.b); }
  // a stock check leaves out the parts a work item on the grid makes, unless told to judge them
  if (kind === 'stock' && r.judgeMade) out.judgeMade = true;
  if (kind === 'formula') out.formula = str(r.formula);
  if (kind === 'lookup') {
    // which source (its code name), which table, its column to match the
    // work item's field (the column's `field`) against, and its column to show
    const lk = r.lookup && typeof r.lookup === 'object' ? r.lookup : {};
    // `sum`: ADD UP every matching row's figure instead of showing the first
    // row's value — a part's stock over every storage location. Kept only
    // when set, so a lookup saved before it normalises as it always did.
    // `per`: the same, added up per value of another column (per storage
    // location). Kept only when set, like `sum`.
    out.lookup = {
      source: str(lk.source).trim(), table: str(lk.table), match: str(lk.match), value: str(lk.value),
      ...(lk.sum && !str(lk.per).trim() ? { sum: true } : {}), ...(str(lk.per).trim() ? { per: str(lk.per).trim() } : {}),
    };
  }
  const f = r.format && typeof r.format === 'object' ? r.format : null;
  if (f) {
    const decimals = f.decimals == null || f.decimals === '' ? null : int(f.decimals, 0, 0, 6);
    out.format = { prefix: str(f.prefix).slice(0, 8), decimals, thousands: [',', ' ', ''].includes(f.thousands) ? f.thousands : ',' };
  }
  const bands = list(r.bands).filter((b) => b && BAND_IDS.includes(b.color))
    .map((b) => ({ below: b.below == null || b.below === '' || !Number.isFinite(Number(b.below)) ? null : Number(b.below), color: b.color }));
  if (bands.length) { out.bands = bands; out.bandAs = r.bandAs === 'text' ? 'text' : 'chip'; }
  return out;
}

export function normalizeColumn(c, i = 0) {
  const r = c && typeof c === 'object' ? c : {};
  const kind = oneOf(r.kind, COLUMN_KINDS, 'text');
  const canAgg = (COLUMN_KINDS.find((k) => k.id === kind) || {}).agg;
  return {
    ...columnOptions(r, kind),
    id: str(r.id) || uid('c'),
    kind,
    label: str(r.label) || (COLUMN_KINDS.find((k) => k.id === kind) || {}).label || 'Column',
    field: str(r.field),
    width: int(r.width, 120, 40, 600),
    align: r.align === 'right' ? 'right' : (r.align === 'center' ? 'center' : 'left'),
    // 'count' is a formula column's: the flag on an item, how many under a row.
    agg: canAgg ? oneOf(r.agg, [{ id: 'none' }, { id: 'sum' }, { id: 'span' }, ...(kind === 'formula' ? [{ id: 'count' }] : [])], 'none') : 'none',
    on: bool(r.on, true),
    // PINNED — an admin's answer to "this column is the point of the page".
    // A reader may resize it, sort by it and read it; they may not put it
    // away. See isPinned in ../shared/userView.
    pinned: bool(r.pinned, false),
    // READ ONLY — the column shows its field and is never typed into on the
    // grid (the work item's own screen still edits it). For dates that must
    // only move with a bar drag, where the Rescheduler takes the work under
    // the bar along — a date typed into one cell would leave it behind.
    // Kept only when set, so every other column normalises as it always did.
    ...(r.readOnly ? { readOnly: true } : {}),
  };
}

// `defaults` is what an UNSET field falls back to, and it is a parameter
// rather than a constant so that a host can hand in its own — a client fork
// keeping a structure page passes its defaults; the product passes none and
// gets the empty structure.
// One dependency rule per kind, in the shape ganttDependencyHelpers expects:
//   { linkTypeId, linkTypeName, predecessorSide: 'inward' | 'outward', lagDays }
// A kind whose link type is unnamed is DROPPED rather than kept as a blank —
// classifyLink wants a truthy linkTypeId, and a half-filled rule that matches
// nothing is worse than no rule, because it reads in the config as if it works.
export const DEPENDENCY_KINDS = ['FS', 'FF', 'SS', 'SF'];

// One button of a cluster on at a time: a second "on by default" of the same
// cluster starts off.
function clusterOn(buttons) {
  const lit = new Set();
  return buttons.map((b) => {
    if (!b.group || !b.on) return b;
    if (lit.has(b.group)) return { ...b, on: false };
    lit.add(b.group);
    return b;
  });
}
// The toolbar's quick buttons in their order, a cluster as ONE part at the
// place of its first button: [{ button } | { group, buttons: [...] }].
export function buttonParts(buttons) {
  const out = []; const at = new Map();
  for (const b of buttons || []) {
    if (!b || !b.group) { out.push({ button: b }); continue; }
    if (!at.has(b.group)) { at.set(b.group, out.length); out.push({ group: b.group, buttons: [] }); }
    out[at.get(b.group)].buttons.push(b);
  }
  return out;
}
// Pressing button `id`: a cluster's other buttons go off with it on; pressing
// the one that is on lets it go. → the new Set of buttons on
export function pressButton(on, buttons, id) {
  const n = new Set(on);
  const b = (buttons || []).find((x) => x && x.id === id);
  if (n.has(id)) { n.delete(id); return n; }
  if (b && b.group) for (const x of buttons) if (x && x.group === b.group) n.delete(x.id);
  n.add(id);
  return n;
}

export function normalizeDependencies(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const out = {};
  for (const kind of DEPENDENCY_KINDS) {
    const r = src[kind];
    if (!r || typeof r !== 'object') continue;
    const linkTypeId = r.linkTypeId == null ? '' : String(r.linkTypeId).trim();
    const linkTypeName = r.linkTypeName == null ? '' : String(r.linkTypeName).trim();
    if (!linkTypeId && !linkTypeName) continue;
    const lag = Number(r.lagDays);
    out[kind] = {
      // classifyLink gates on linkTypeId, then matches by id OR name. A rule
      // named only by name still has to pass that gate, so the name stands in.
      linkTypeId: linkTypeId || linkTypeName,
      linkTypeName,
      predecessorSide: r.predecessorSide === 'outward' ? 'outward' : 'inward',
      lagDays: Number.isFinite(lag) ? lag : 0,
    };
  }
  return out;
}

export function normalizeConfig(raw, defaults = DEFAULTS) {
  const D = defaults && typeof defaults === 'object' ? defaults : DEFAULTS;
  const r = raw && typeof raw === 'object' ? raw : {};
  const g = r.gantt && typeof r.gantt === 'object' ? r.gantt : {};
  const folders = fixFolderParents((r.folders === undefined ? clone(D.folders) : list(r.folders)).map(normalizeFolder));
  const columns = r.columns === undefined ? clone(D.columns) : list(r.columns).map(normalizeColumn);
  return {
    filterZql: r.filterZql === undefined ? D.filterZql : str(r.filterZql),
    sortRoot: sortOf(r.sortRoot, D.sortRoot),
    filterMode: oneOf(r.filterMode, FILTER_MODES, D.filterMode),
    maxExcluded: int(r.maxExcluded, D.maxExcluded, 0, 200000),
    // EVERY GRID LOADS ON THE SERVER, whatever an older config saved. Measured
    // on the WIP grid (2026-10-01): the browser walk took 41 s and 133
    // requests; the server walk is one gzipped stream in well under half
    // the time. It is not a setting any more — nothing gained by the other
    // two was worth that, and a saved 'walk' kept the slow path alive. The
    // browser loaders stay in the page as code, unreachable from a config.
    loadMode: 'server',
    universeZql: str(r.universeZql, D.universeZql),
    maxRows: int(r.maxRows, D.maxRows, 100, 200000),
    pageSize: int(r.pageSize, D.pageSize, 50, 5000),
    walkChunk: int(r.walkChunk, D.walkChunk, 10, 1000),
    walkParallel: int(r.walkParallel, D.walkParallel, 1, 12),
    folders: folders.length ? folders : fixFolderParents(clone(D.folders).map(normalizeFolder)),
    columns: columns.length ? columns : clone(D.columns),
    filters: r.filters === undefined ? clone(D.filters)
      : list(r.filters).map((f, i) => (f && f.field ? { id: str(f.id) || `f${i}`, label: str(f.label), field: str(f.field) } : null)).filter(Boolean),
    // A LENS button (lens: true) only hides rows: the counts, sums and
    // progress beside the rows it leaves still add up everything under them —
    // "only the orders and their assemblies" must not make an order's
    // progress forget the operations it hides. Kept only when set.
    // A CLUSTER (group: a name) draws the buttons of one name as ONE segmented
    // control, like Show Done · Hide Done · Done only — one of them on at a
    // time (pressing the one that is on lets it go: everything again); only
    // the first of a cluster may start on. iconType: a work type's name — the
    // site's own icon of it sits before the caption. Both kept only when set.
    buttons: r.buttons === undefined ? clone(D.buttons)
      : clusterOn(list(r.buttons).map((b, i) => ({
        id: str(b && b.id) || `b${i}`, caption: str(b && b.caption), zql: str(b && b.zql), on: !!(b && b.on),
        ...(b && b.lens ? { lens: true } : {}),
        ...(b && str(b.group).trim() ? { group: str(b.group).trim() } : {}),
        ...(b && str(b.iconType).trim() ? { iconType: str(b.iconType).trim() } : {}),
      }))),
    buttonsMode: r.buttonsMode === 'any' ? 'any' : 'all',
    // 'tree' (the default): a button keeps the rows it matches, anywhere, and
    // the rows leading to them. 'roots': a button narrows each folder's
    // INSERTED rows and keeps everything under the ones it passes — a query
    // AND-ed into the insert.
    buttonsScope: r.buttonsScope === 'roots' ? 'roots' : (r.buttonsScope === undefined && D.buttonsScope === 'roots' ? 'roots' : 'tree'),
    hideDone: oneOf(r.hideDone, DONE_MODES, D.hideDone),
    openDepth: int(r.openDepth, D.openDepth, 0, 20),
    showRules: bool(r.showRules, D.showRules),
    // The timeline lives inside the grid, which already fills the window,
    // so there is nothing left for a height knob to mean. A height saved by
    // an older config is simply dropped.
    gantt: {
      on: bool(g.on, D.gantt.on),
      columns: g.columns === undefined ? D.gantt.columns.slice() : list(g.columns).map(String),
      dependencies: normalizeDependencies(g.dependencies),
    },
    editable: bool(r.editable, D.editable),
    // A host's own defaults may predate the knob; unset there is the default.
    frozenColumns: Array.isArray(r.frozenColumns) ? [...new Set(r.frozenColumns.map((id) => str(id)).filter(Boolean))]
      : (r.frozenColumns === undefined && Array.isArray(D.frozenColumns) ? D.frozenColumns.slice() : null),
    cloneTypes: r.cloneTypes === undefined ? (D.cloneTypes || []).slice()
      : [...new Set(list(r.cloneTypes).map((t) => str(t).trim()).filter(Boolean))],
    cloneAttention: r.cloneAttention === undefined ? (D.cloneAttention || []).slice()
      : [...new Set(list(r.cloneAttention).map((t) => str(t).trim()).filter(Boolean))],
    cloneGroups: r.cloneGroups === undefined ? (D.cloneGroups || []).slice()
      : [...new Set(list(r.cloneGroups).map((t) => str(t).trim()).filter(Boolean))],
    // The Rescheduler: off, site admins only, no work types — unless saved.
    ...rescheduleSettingsOf(r, D),
    headWrap: r.headWrap === 'full' || r.headWrap === 'fit' ? r.headWrap : (D.headWrap === 'full' ? 'full' : 'fit'),
    layouts: r.layouts && typeof r.layouts === 'object' ? r.layouts : {},
  };
}

// THE RESCHEDULER'S THREE KEYS (./reschedule.js holds its rules), normalised
// HERE rather than imported from there. This file is copied into several
// client forks' own suites together with a fixed list of its neighbours, and
// a new import would turn every one of those builds red the day the product
// is merged in. ./reschedule.js keeps the twin the server loads
// (rescheduleSettingsOf); test/run-rescheduler-tests.mjs proves the two
// answer the same. Change both or neither.
const rsNames = (v) => [...new Set(list(v).map((g) => str(g).trim()).filter(Boolean))];
function rsTypes(raw) {
  const out = [];
  const seen = new Set();
  list(raw).forEach((r) => {
    const o = typeof r === 'string' ? { type: r } : (r && typeof r === 'object' ? r : null);
    const type = o ? str(o.type).trim() : '';
    if (!type || seen.has(type.toLowerCase())) return;
    seen.add(type.toLowerCase());
    out.push({ type, skipDone: o.skipDone === undefined ? true : !!o.skipDone, skipInProgress: !!o.skipInProgress });
  });
  return out;
}
export function rescheduleSettingsOf(raw, defaults = {}) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const D = defaults && typeof defaults === 'object' ? defaults : {};
  return {
    rescheduleOn: r.rescheduleOn === undefined ? !!D.rescheduleOn : !!r.rescheduleOn,
    rescheduleGroups: rsNames(r.rescheduleGroups === undefined ? D.rescheduleGroups : r.rescheduleGroups),
    rescheduleTypes: rsTypes(r.rescheduleTypes === undefined ? D.rescheduleTypes : r.rescheduleTypes),
  };
}

// A grid's RESCHEDULER SETTINGS, lifted off its saved structure — what an
// admin copies onto another grid (Page settings → Rescheduler → copy from a
// grid), as the cloning settings are.
export function rescheduleOf(raw) {
  return rescheduleSettingsOf(raw && typeof raw === 'object' ? raw : {}, EMPTY_STRUCTURE);
}

// Whether this reader may clone on this grid: anyone when no group is named,
// an admin always, otherwise a member of one of the named groups. The
// button and the sheep are drawn only for them.
export const mayClone = (cfg, userGroups, isAdmin = false) => {
  const want = (cfg && cfg.cloneGroups) || [];
  if (!want.length || isAdmin) return true;
  const mine = new Set((Array.isArray(userGroups) ? userGroups : []).map(String));
  return want.some((g) => mine.has(String(g)));
};

// A grid's CLONING SETTINGS, lifted off its saved structure — what an admin
// copies onto another grid (Page settings → Cloning → copy from a grid).
export const CLONE_KEYS = ['cloneTypes', 'cloneAttention', 'cloneGroups'];
export function cloneSettingsOf(raw) {
  const c = normalizeConfig(raw && typeof raw === 'object' ? raw : {}, EMPTY_STRUCTURE);
  return { cloneTypes: c.cloneTypes, cloneAttention: c.cloneAttention, cloneGroups: c.cloneGroups };
}

// The ids of the columns pinned at the left, in the columns' own order: the
// admin's list when there is one, otherwise every Key and Summary column.
export const frozenIdsOf = (cfg) => {
  const cols = (cfg && cfg.columns) || [];
  const list = cfg && Array.isArray(cfg.frozenColumns) ? new Set(cfg.frozenColumns) : null;
  return cols.filter((c) => (list ? list.has(c.id) : (c.kind === 'key' || c.kind === 'summary'))).map((c) => c.id);
};

// Whether a work item's type is one the admin opened up for cloning.
export const isCloneable = (cfg, item) => {
  const name = String((item && item.fields && item.fields.issuetype && item.fields.issuetype.name) || '').trim().toLowerCase();
  return !!name && (cfg.cloneTypes || []).some((t) => String(t).trim().toLowerCase() === name);
};

// Every field NAME the config asks for — what the page resolves against the
// site before it loads a single row.
export function fieldNamesOf(cfg) {
  const out = new Set();
  (cfg.columns || []).forEach((c) => { if (c.field) out.add(c.field); });
  (cfg.filters || []).forEach((f) => { if (f.field) out.add(f.field); });
  (cfg.folders || []).forEach((f) => (f.groups || []).forEach((g) => { if (g.field && (g.kind === 'field' || g.kind === 'date' || !g.kind)) out.add(g.field); }));
  // A folder's table lines: the field the table is in, and the fields its lines fill.
  (cfg.folders || []).forEach((f) => {
    const l = f.expand && f.expand.lines;
    if (!l) return;
    if (l.field) out.add(l.field);
    (l.map || []).forEach((m) => { if (m.field) out.add(m.field); });
  });
  // A folder's children grouped by a field: that field.
  (cfg.folders || []).forEach((f) => { if (f.groupChildren && f.groupChildren.field) out.add(f.groupChildren.field); });
  // What the sorts and the formulas read — a field nothing else asks for
  // would otherwise never be loaded, and the sort or the formula would see
  // blanks on every row.
  [cfg.sortRoot, ...(cfg.folders || []).map((f) => f.sort)].forEach((s) => { const n = sortFieldOf(s); if (n) out.add(n); });
  formulaNamesOf(cfg).forEach((n) => out.add(n));
  return [...out];
}

// The field names every formula in the config reads — formula columns and
// formula groupings alike (see formulaFieldNames in ./grouping.js).
export function formulaNamesOf(cfg) {
  const out = new Set();
  (cfg.columns || []).forEach((c) => { if (c.kind === 'formula') formulaFieldNames(c.formula).forEach((n) => out.add(n)); });
  (cfg.folders || []).forEach((f) => (f.groups || []).forEach((g) => { if (g.kind === 'formula') formulaFieldNames(g.formula).forEach((n) => out.add(n)); }));
  return [...out];
}

// Every link type the structure asks for, across every folder — what the
// "walk" loader needs so it can ask for issuelinks only when something uses
// them.
export function usesLinks(cfg) {
  return (cfg.folders || []).some((f) => f.expand && (f.expand.links || []).length);
}

export const newFolder = (parentId = null) => normalizeFolder({
  id: uid('f'), name: 'New folder', icon: 'folder', inserts: [{ id: 'i0', zql: '' }],
  expand: { byParent: true, links: [], maxDepth: 8 }, groups: [], sort: 'summary', parentId,
});
export const newColumn = (over = {}) => normalizeColumn({ id: uid('c'), kind: 'text', label: 'New column', field: '', width: 140, ...over });
export const newLink = () => normalizeLink({ id: uid('l'), type: 'Link', relation: 'Link to Child' });
export { uid };

// ---- removing a rule -----------------------------------------------------------
// ONE PLACE says what a rule's removal does, for the Remove at the foot of its
// editor and the bin beside its amber line alike, so the two cannot drift.
// `edit` is the amber row's descriptor ({ what, folderId, index }). Answers
// { draft, folder } — the draft without the rule, and for a folder its name
// and how many folders inside it go with it (the caller asks first) — or null
// when that rule cannot be removed (a sort: there is always one; an unset
// filter: there is nothing to remove; an "add" line).
export function withoutRule(draft, edit) {
  if (!draft || !edit) return null;
  const { what, folderId } = edit;
  const index = typeof edit.index === 'number' ? edit.index : 0;
  if (what === 'filter') return (draft.filterZql || '').trim() ? { draft: { ...draft, filterZql: '' } } : null;
  const f = (draft.folders || []).find((x) => x.id === folderId);
  if (!f) return null;
  const put = (patch) => ({ draft: { ...draft, folders: draft.folders.map((x) => (x.id === folderId ? { ...x, ...patch } : x)) } });
  if (what === 'folder') {
    const inside = descendantFolderIds(draft.folders, folderId);
    return {
      draft: { ...draft, folders: draft.folders.filter((x) => x.id !== folderId && !inside.has(x.id)) },
      folder: { name: f.name, inside: inside.size },
    };
  }
  if (what === 'insert') return (f.inserts || [])[index] ? put({ inserts: f.inserts.filter((_, j) => j !== index) }) : null;
  if (what === 'link') return (f.expand.links || [])[index] ? put({ expand: { ...f.expand, links: f.expand.links.filter((_, j) => j !== index) } }) : null;
  if (what === 'group') return (f.groups || [])[index] ? put({ groups: f.groups.filter((_, j) => j !== index) }) : null;
  if (what === 'parent') return f.expand.byParent ? put({ expand: { ...f.expand, byParent: false } }) : null;
  if (what === 'lines') {
    if (!f.expand.lines) return null;
    const { lines, ...rest } = f.expand; // eslint-disable-line no-unused-vars
    return put({ expand: rest });
  }
  if (what === 'childGroups') {
    if (!f.groupChildren) return null;
    const { groupChildren, ...rest } = f; // eslint-disable-line no-unused-vars
    return { draft: { ...draft, folders: draft.folders.map((x) => (x.id === folderId ? rest : x)) } };
  }
  if (what === 'folderFilter') return (f.filterZql || '').trim() ? put({ filterZql: '' }) : null;
  return null;
}

// The question asked before a folder goes.
export const removeFolderQuestion = ({ name, inside }) => `${inside
  ? `Remove the folder “${name}” and the ${inside === 1 ? 'folder' : `${inside} folders`} inside it, with all their rules?`
  : `Remove the folder “${name}” with all its rules?`}\n\nThe work items are not touched. Nothing is gone until you Save.`;
